import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// Google Identity Services types
declare global {
  interface Window {
    google: any;
  }
}

export interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
  tenantId?: string;
  hostedDomain?: string;
}

export interface GoogleAuthResponse {
  idToken: string;
  user: GoogleUser;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private _isGoogleAuthenticated = new BehaviorSubject<boolean>(false);
  isGoogleAuthenticated$: Observable<boolean> = this._isGoogleAuthenticated.asObservable();
  
  private _googleUser = new BehaviorSubject<GoogleUser | null>(null);
  googleUser$: Observable<GoogleUser | null> = this._googleUser.asObservable();

  private isInitialized = false;
  private isGoogleScriptLoaded = false;

  constructor() {
    this.initializeGoogleAuth();
    
    // Check if we're returning from Google OAuth
    this.handleOAuthCallback();
  }

  /**
   * Initialize Google Identity Services
   */
  private async initializeGoogleAuth(): Promise<void> {
    try {
      // Check if we're in browser environment
      if (typeof window === 'undefined') {
        console.warn('Google Auth: Not in browser environment');
        return;
      }

      // Load Google Identity Services script
      await this.loadGoogleScript();
      
      // Initialize Google Identity Services
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: this.handleCredentialResponse.bind(this),
          auto_select: false,
          cancel_on_tap_outside: true
        });

        this.isInitialized = true;
        console.log('Google Identity Services initialized successfully');
        
        // Check if user is already authenticated
        this.checkExistingAuth();
      } else {
        throw new Error('Google Identity Services not available');
      }
    } catch (error) {
      console.error('Failed to initialize Google Auth:', error);
    }
  }

  /**
   * Load Google Identity Services script
   */
  private loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isGoogleScriptLoaded || (window.google && window.google.accounts)) {
        this.isGoogleScriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        this.isGoogleScriptLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Handle credential response from Google
   */
  private async handleCredentialResponse(response: any): Promise<void> {
    try {
      if (!response.credential) {
        throw new Error('No credential received from Google');
      }

      const idToken = response.credential;
      
      // Decode and log the full ID token payload
      try {
        const tokenParts = idToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('Decoded Google ID token payload (from Identity Services):', payload);
        }
      } catch (decodeError) {
        console.error('Error decoding ID token for logging:', decodeError);
      }
      
      // Decode JWT token to get user info
      const user = this.decodeJwtToken(idToken);
      
      if (!user) {
        throw new Error('Failed to decode user information');
      }

      // Store authentication data
      localStorage.setItem('google_id_token', idToken);
      localStorage.setItem('google_user', JSON.stringify(user));

      // Update state
      this._googleUser.next(user);
      this._isGoogleAuthenticated.next(true);

      console.log('Google login successful:', user);

    } catch (error) {
      console.error('Error handling Google credential response:', error);
      throw error;
    }
  }

  /**
   * Decode JWT token to extract user information
   */
  private decodeJwtToken(token: string): GoogleUser | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);

      const tenantId = payload.tid || payload.tenantId || payload.tenant_id || payload.hd || '';
      return {
        id: payload.sub,
        email: payload.email || '',
        name: payload.name || '',
        picture: payload.picture || '',
        given_name: payload.given_name || '',
        family_name: payload.family_name || '',
        tenantId: tenantId || undefined,
        hostedDomain: payload.hd || undefined
      };
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Check if user is already authenticated from stored tokens
   */
  private checkExistingAuth(): void {
    try {
      const storedIdToken = localStorage.getItem('google_id_token');
      const storedUser = localStorage.getItem('google_user');

      if (storedIdToken && storedUser) {
        // Check if token is expired
        const user = JSON.parse(storedUser);
        const tokenPayload = this.decodeJwtToken(storedIdToken);
        
        if (tokenPayload && this.isTokenValid(storedIdToken)) {
          this._googleUser.next(user);
          this._isGoogleAuthenticated.next(true);
          console.log('Google Auth: Restored existing session');
        } else {
          // Token is invalid or expired, clear stored data
          this.clearStoredAuth();
        }
      }
    } catch (error) {
      console.error('Error checking existing Google auth:', error);
      this.clearStoredAuth();
    }
  }

  /**
   * Check if JWT token is still valid (not expired)
   */
  private isTokenValid(token: string): boolean {
    try {
      const payload = this.decodeJwtToken(token);
      if (!payload) return false;

      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      const currentTime = Math.floor(Date.now() / 1000);
      
      return decoded.exp > currentTime;
    } catch (error) {
      return false;
    }
  }

  /**
   * Initiate Google OAuth login flow (redirect to Google like Microsoft)
   */
  async login(): Promise<GoogleAuthResponse> {
    console.log('Initiating Google OAuth redirect...');
    
    // Redirect to Google OAuth (like Microsoft login)
    this.initiateOAuthRedirect();
    
    // This will never resolve because we're redirecting away from the page
    // The authentication completion will be handled when Google redirects back
    return new Promise(() => {
      // This promise never resolves because we redirect away
    });
  }

  /**
   * Initiate Google OAuth redirect flow (like Microsoft login)
   */
  private initiateOAuthRedirect(): void {
    try {
      console.log('Initiating Google OAuth redirect flow...');
      
      const clientId = environment.googleClientId;
      // Use login page as redirect URI
      const redirectUri = `${window.location.origin}/login`;
      const scope = 'openid email profile';
      const responseType = 'id_token'; // Use implicit flow for SPA
      const nonce = this.generateNonce(); // For security
      
      // Store nonce for validation when user returns
      localStorage.setItem('google_oauth_nonce', nonce);
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${encodeURIComponent(responseType)}&` +
        `nonce=${encodeURIComponent(nonce)}&` +
        `prompt=select_account`;

      console.log('Redirecting to Google OAuth:', authUrl);
      console.log('Redirect URI being used:', redirectUri);
      console.log('Client ID being used:', clientId);
      
      // Redirect to Google (like Microsoft login)
      window.location.href = authUrl;

    } catch (error) {
      console.error('Error in OAuth redirect:', error);
      throw error;
    }
  }

  /**
   * Generate a random nonce/state for OAuth security
   */
  private generateNonce(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Fallback: Create mock authentication for testing when OAuth redirect isn't suitable
   */
  private initiatePopupFlow(
    resolve: (value: GoogleAuthResponse) => void,
    reject: (reason: any) => void
  ): void {
    try {
      console.log('Using mock Google authentication for testing...');
      console.log('In production, this would redirect to Google OAuth like Microsoft login');
      
      const mockUser: GoogleUser = {
        id: 'mock_google_user_id',
        email: 'test@gmail.com',
        name: 'Test User',
        picture: 'https://via.placeholder.com/96x96.png?text=User',
        given_name: 'Test',
        family_name: 'User'
      };

      const mockIdToken = this.createMockIdToken(mockUser);

      // Store authentication data
      localStorage.setItem('google_id_token', mockIdToken);
      localStorage.setItem('google_user', JSON.stringify(mockUser));

      // Update state
      this._googleUser.next(mockUser);
      this._isGoogleAuthenticated.next(true);

      console.log('Mock Google authentication successful:', mockUser);

      resolve({
        idToken: mockIdToken,
        user: mockUser
      });

    } catch (error) {
      console.error('Error in popup flow:', error);
      reject(error);
    }
  }

  /**
   * Create a mock ID token for testing
   */
  private createMockIdToken(user: GoogleUser): string {
    const header = {
      alg: 'RS256',
      typ: 'JWT',
      kid: 'mock_key_id'
    };

    const payload = {
      iss: 'https://accounts.google.com',
      aud: environment.googleClientId,
      sub: user.id,
      email: user.email,
      name: user.name,
      picture: user.picture,
      given_name: user.given_name,
      family_name: user.family_name,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600 // 1 hour
    };

    // Create a mock JWT (not cryptographically signed, for testing only)
    const encodedHeader = btoa(JSON.stringify(header));
    const encodedPayload = btoa(JSON.stringify(payload));
    const mockSignature = 'mock_signature_for_testing';

    return `${encodedHeader}.${encodedPayload}.${mockSignature}`;
  }

  /**
   * Logout from Google authentication
   */
  logout(): void {
    try {
      // Sign out from Google if available
      if (window.google && window.google.accounts && window.google.accounts.id) {
        window.google.accounts.id.disableAutoSelect();
      }
    } catch (error) {
      console.warn('Error during Google sign out:', error);
    }

    this.clearStoredAuth();
    this._googleUser.next(null);
    this._isGoogleAuthenticated.next(false);
    console.log('Google logout successful');
  }

  /**
   * Clear stored authentication data
   */
  private clearStoredAuth(): void {
    localStorage.removeItem('google_id_token');
    localStorage.removeItem('google_user');
  }

  /**
   * Get current Google user
   */
  getCurrentUser(): GoogleUser | null {
    return this._googleUser.value;
  }

  /**
   * Get stored Google ID token
   */
  getIdToken(): string | null {
    return localStorage.getItem('google_id_token');
  }

  /**
   * Check if user is authenticated with Google
   */
  isAuthenticated(): boolean {
    return this._isGoogleAuthenticated.value;
  }

  /**
   * Handle OAuth callback when Google redirects back to the app
   */
  private handleOAuthCallback(): void {
    // Check URL fragment for implicit flow response
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    
    const idToken = params.get('id_token');
    const error = params.get('error');

    if (error) {
      console.error('Google OAuth error:', error);
      return;
    }

    if (idToken) {
      console.log('Google OAuth callback received with ID token');
      
      // Verify nonce for security
      const storedNonce = localStorage.getItem('google_oauth_nonce');
      
      let payload: any = null;
      // Decode the JWT payload directly to access nonce
      const tokenParts = idToken.split('.');
      if (tokenParts.length === 3) {
        payload = JSON.parse(atob(tokenParts[1]));
        if (payload.nonce !== storedNonce) {
          console.error('OAuth nonce mismatch - possible CSRF attack');
          return;
        }
      }

      // Clean up nonce
      localStorage.removeItem('google_oauth_nonce');

      if (payload) {
        console.log('Decoded Google ID token payload:', payload);
      }

      // Process the ID token directly
      this.processIdTokenFromCallback(idToken);
    }
  }

  /**
   * Process ID token received from OAuth callback
   */
  private async processIdTokenFromCallback(idToken: string): Promise<void> {
    try {
      console.log('Processing ID token from OAuth callback...');
      
      // Decode the real ID token from Google
      const user = this.decodeJwtToken(idToken);
      
      if (!user) {
        console.error('Failed to decode Google ID token');
        return;
      }

      // Store authentication data
      localStorage.setItem('google_id_token', idToken);
      localStorage.setItem('google_user', JSON.stringify(user));

      // Update state
      this._googleUser.next(user);
      this._isGoogleAuthenticated.next(true);

      console.log('Google OAuth authentication successful with real user:', user);
      if (user.tenantId) {
        console.log('Google tenant ID (Google Workspace domain):', user.tenantId);
      } else if (user.hostedDomain) {
        console.log('Google hosted domain:', user.hostedDomain);
      } else {
        console.log('Google tenant/hosted domain not present (personal Gmail account).');
      }

      // Clean up URL fragment
      if (window.location.hash) {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
      
    } catch (error) {
      console.error('Error processing ID token from callback:', error);
    }
  }

  /**
   * Render Google Sign-In button in a specific element
   */
  renderButton(element: HTMLElement, options?: any): void {
    if (!this.isInitialized || !window.google || !window.google.accounts) {
      console.error('Google Auth not initialized');
      return;
    }

    const defaultOptions = {
      theme: 'outline',
      size: 'large',
      type: 'standard',
      shape: 'rectangular',
      text: 'signin_with',
      logo_alignment: 'left'
    };

    window.google.accounts.id.renderButton(element, { ...defaultOptions, ...options });
  }
}
